document.documentElement.classList.remove('no-js');

class StoreHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.navigation = this.querySelector('[data-mobile-navigation]');
    this.openLabel = this.querySelector('.site-header__menu-open');
    this.closeLabel = this.querySelector('.site-header__menu-close');

    if (!this.toggle || !this.navigation) return;

    // Pages restored from the back-forward cache can preserve element state.
    // Re-establish the documented initial state on every connection.
    this.setOpen(false);
    this.updateScrollState();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.toggle.addEventListener('click', () => this.setOpen(this.navigation.hidden), { signal });
    this.navigation.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.setOpen(false);
    }, { signal });
    window.addEventListener('scroll', () => this.updateScrollState(), { passive: true, signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.navigation.hidden) {
        this.setOpen(false);
        this.toggle.focus();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    document.body.classList.remove('menu-open');
  }

  setOpen(open) {
    this.navigation.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.openLabel.hidden = open;
    this.closeLabel.hidden = !open;
    document.body.classList.toggle('menu-open', open);
  }

  updateScrollState() {
    this.classList.toggle('is-scrolled', window.scrollY > 24);
  }
}

class ProductPurchase extends HTMLElement {
  connectedCallback() {
    this.variantControls = [...this.querySelectorAll('[data-variant-id]')];
    this.variantInput = this.querySelector('[data-variant-input]');
    this.price = this.querySelector('[data-price]');
    this.currentPrice = this.querySelector('[data-current-price]');
    this.currentVariant = this.querySelector('[data-current-variant]');
    this.comparePrice = this.querySelector('[data-compare-price]');
    this.availability = this.querySelector('[data-availability]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');
    this.stickyVariant = this.querySelector('[data-sticky-variant]');
    this.stickyAddButton = this.querySelector('[data-sticky-add-button]');
    this.stickyAddLabel = this.querySelector('[data-sticky-add-label]');
    this.stickyBar = this.querySelector('[data-mobile-purchase-bar]');
    this.stickyActivationTarget = this.querySelector('[data-sticky-activation-target]') || this.querySelector('.product-form');
    this.liveRegion = this.querySelector('[data-variant-live-region]');
    this.commerceEnabled = this.dataset.commerceEnabled === 'true';

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    if (this.stickyBar && this.stickyActivationTarget) {
      this.setStickyVisible(false);
      requestAnimationFrame(() => requestAnimationFrame(() => this.updateStickyVisibility()));
      window.addEventListener('load', () => this.updateStickyVisibility(), { once: true, signal });
      window.addEventListener('scroll', () => this.updateStickyVisibility(), { passive: true, signal });
      window.addEventListener('resize', () => this.updateStickyVisibility(), { passive: true, signal });
    } else if (this.stickyBar) {
      this.setStickyVisible(false);
    }

    if (this.variantControls.length === 0) return;

    for (const control of this.variantControls) {
      control.addEventListener('change', (event) => this.selectVariant(event.currentTarget), { signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  selectVariant(control) {
    const available = control.dataset.variantAvailable === 'true';
    const price = control.dataset.variantPrice || '';
    const compare = control.dataset.variantCompare || '';
    const title = control.dataset.variantTitle || '';
    const availabilityText = available ? this.dataset.labelAvailable : this.dataset.labelSoldOut;

    if (this.variantInput) this.variantInput.value = control.dataset.variantId;

    if (this.currentPrice) this.currentPrice.textContent = price;
    if (this.currentVariant) this.currentVariant.textContent = title;
    if (this.stickyPrice) this.stickyPrice.textContent = price;
    if (this.stickyVariant) this.stickyVariant.textContent = title;
    if (this.comparePrice) {
      this.comparePrice.textContent = compare;
      this.comparePrice.hidden = compare.length === 0 || compare === price;
    }
    if (this.price) {
      this.price.setAttribute('aria-label', compare && compare !== price ? `${price}, ${compare}` : price);
    }
    if (this.availability) {
      this.availability.textContent = availabilityText;
      this.availability.classList.toggle('availability--available', available);
      this.availability.classList.toggle('availability--unavailable', !available);
    }

    if (this.addButton) {
      this.addButton.disabled = !available || !this.commerceEnabled;
    }
    if (this.addLabel) {
      this.addLabel.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
    }
    if (this.stickyAddButton) {
      this.stickyAddButton.disabled = !available || !this.commerceEnabled;
    }
    if (this.stickyAddLabel) {
      this.stickyAddLabel.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
    }

    this.announce(title, price, availabilityText);
  }

  updateStickyVisibility() {
    const hasPassedInlineForm = this.stickyActivationTarget.getBoundingClientRect().bottom <= 0;
    this.setStickyVisible(hasPassedInlineForm);
  }

  setStickyVisible(visible) {
    if (!this.stickyBar) return;
    this.stickyBar.hidden = !visible;
    this.stickyBar.classList.toggle('is-active', visible);
    this.stickyBar.setAttribute('aria-hidden', String(!visible));
  }

  announce(variant, price, availability) {
    if (!this.liveRegion) return;

    const template = this.dataset.liveTemplate || '__VARIANT__. __PRICE__. __AVAILABILITY__';
    const message = template
      .replace('__VARIANT__', variant)
      .replace('__PRICE__', price)
      .replace('__AVAILABILITY__', availability);

    this.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      this.liveRegion.textContent = message;
    });
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-gallery-stage]');
    this.image = this.querySelector('[data-gallery-image]');
    this.caption = this.querySelector('[data-gallery-caption]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.stage || !this.image || this.thumbs.length === 0) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.select(thumb), { signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  select(thumb) {
    // Each frame has its own aspect ratio. Carrying the ratio onto the stage
    // keeps the whole object in view instead of cropping it to a fixed box.
    const { full, ratio, alt, caption } = thumb.dataset;
    if (full) {
      this.image.removeAttribute('srcset');
      this.image.removeAttribute('sizes');
      this.image.src = full;
    }
    if (alt) this.image.alt = alt;
    if (ratio) this.stage.style.aspectRatio = ratio;
    if (caption && this.caption) this.caption.textContent = caption;

    for (const other of this.thumbs) {
      other.setAttribute('aria-current', String(other === thumb));
    }
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

class LazyVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    if (!('IntersectionObserver' in window)) return this.attach();
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) { this.attach(); this.observer.disconnect(); }
      }
    }, { rootMargin: '300px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() { this.observer?.disconnect(); }

  attach() {
    if (this.attached) return;
    this.attached = true;
    const wide = matchMedia('(min-width: 750px)').matches;
    const src = wide ? this.dataset.srcDesktop : this.dataset.srcMobile;
    if (!src) return;
    const source = document.createElement('source');
    source.type = 'video/mp4';
    source.src = src;
    this.video.appendChild(source);
    this.video.load();
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // play() rejects if it is called before the element can actually start,
    // and the promise rejection is silent — the first attempt here failed that
    // way and left the poster up. Retry once the browser says it can play.
    const sync = () => { if (this.button) this.button.hidden = !this.video.paused; };
    const start = () => this.video.play().then(sync).catch(sync);
    this.video.addEventListener('canplay', start, { once: true });
    this.video.addEventListener('play', sync);
    this.video.addEventListener('pause', sync);
    this.button = this.querySelector('[data-motion-play]');
    this.button?.addEventListener('click', () => {
      if (this.video.paused) this.video.play().then(sync).catch(sync); else { this.video.pause(); sync(); }
    });
    start();
  }
}

if (!customElements.get('lazy-video')) customElements.define('lazy-video', LazyVideo);

if (!customElements.get('store-header')) customElements.define('store-header', StoreHeader);
if (!customElements.get('product-purchase')) customElements.define('product-purchase', ProductPurchase);
